<template>
  <section
    v-aha-emit-action.view
    name="onboardingFeatureBasedOptions"
    class="onboarding-container"
  >
    <LoadingScreen v-if="isLoadingFeatures" />
    <transition
      name="slide-right"
      enter-active-class="animate__animated animate__slideInRight animate__faster"
      leave-active-class="animate__animated animate__slideOutRight animate__faster"
    >
      <div v-if="!isLoadingFeatures" class="container feature-based-options-container">
        <section class="feature-based-options">
          <div class="text-right pt-6 pb-10">
            <span
              name="onboardingFeatureBasedOptionsSkipButton"
              v-aha-emit-action
              class="cursor-pointer underline skip text-base-70"
              @click="skip"
            >
              {{ $i18nPresenter.t('Skip') }}
            </span>
          </div>
          <div class="section-wrapper">
            <div class="section-column section-options">
              <div class="font-bold text-2xl mb-8 fs-28">
                {{ $i18nPresenter.t('Create your first slide:') }}
              </div>
              <div class="options-wrapper">
                <FeatureOptionCard
                  name="onboardingOption"
                  v-aha-emit-action
                  :aha-other-info="blankOption.key"
                  :option="blankOption"
                  @preview="preview"
                  @click="handleClickOnboardingOption"
                />
                <FeatureOptionCard
                  name="onboardingOption"
                  v-aha-emit-action
                  :aha-other-info="option.key"
                  v-for="option in featureBasedOptions"
                  :key="option.key"
                  :option="option"
                  :data-presentation-id="option.presentationId"
                  @preview="preview"
                  @click="handleClickOnboardingOption"
                />
              </div>
              <div class="divider my-5 uppercase">
                <span>{{ $i18nPresenter.t('or') }}</span>
              </div>
              <FeatureOptionCard
                v-aha-emit-action
                name="onboardingOption"
                :aha-other-info="templateOption.key"
                :option="templateOption"
                @preview="preview"
                @click="browseTemplates"
              >
                <div class="option-card-title font-bold text-base flex items-center">
                  <span class="option-card-img">
                    <aha-icon
                      :name="templateOption.icon"
                      maintainColor
                    />
                  </span>
                  <span class="align-middle">{{ $i18nPresenter.t('Explore ready-made slides') }}</span>
                </div>
              </FeatureOptionCard>
            </div>
            <div class="section-column section-preview">
              <div class="preview-card">
                <video
                  class="preview-source default"
                  :src="defaultFeaturePreviewSource"
                  autoplay
                  loop
                  muted
                ></video>
                <video
                  v-if="previewSource && previewSource.includes('.webm')"
                  class="preview-source"
                  :src="previewSource"
                  autoplay
                  loop
                  muted
                ></video>
                <img
                  v-else-if="previewSource"
                  class="preview-source"
                  :src="previewSource"
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </transition>
  </section>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import CreateNewPresentationMixin from '@/mixins/presentation/CreateNewPresentationMixin'
import configs from '@/config'
import utils from '@/util'
import FeatureOptionCard from '@/components-v2/onboarding/FeatureOptionCard.vue'
import LoadingScreen from '@/components-v2/common/loading/LoadingScreen.vue'

const CURRENT_LOCALES = ['en', 'es', 'fr', 'zh', 'ru', 'vi']

export default {
  components: {
    FeatureOptionCard,
    LoadingScreen,
  },
  mixins: [CreateNewPresentationMixin],
  data() {
    return {
      previewSource: null,
    }
  },
  computed: {
    ...mapGetters('onBoarding', ['isLoadingFeatures']),
    ...mapState({
      currentUser: (state) => state.currentUser,
      featureBasedOptions: (state) => state.onBoarding.featureBasedOptions,
      presenterLocale: (state) => state.presenterLocale,
    }),
    lang() {
      return CURRENT_LOCALES.includes(this.presenterLocale) ? this.presenterLocale : 'en'
    },
    blankOption() {
      return {
        key: 'blank',
        name: this.$i18nPresenter.t('Blank'),
        previewSource: `${configs.cloudfrontPublic}/feature-based-options/${this.lang}/blank.svg`,
        icon: 'add-colored',
      }
    },
    defaultFeaturePreviewSource() {
      return `${configs.cloudfrontPublic}/feature-based-options/${this.lang}/default.webm`
    },
    templateOption() {
      return {
        key: 'templates',
        name: 'Templates',
        previewSource: `${configs.cloudfrontPublic}/feature-based-options/${this.lang}/templates.webm`,
        icon: 'magic-wand-colored',
      }
    },
  },
  watch: {
    featureBasedOptions: {
      handler(options) {
        if (options && options.length) {
          this.preloadAssets(options)
        }
      },
      immediate: true,
    },
  },
  async created() {
    this.$emit('created')
    await this.getFeatureBasedOptionsAction({
      language: this.lang,
      useAhaSlidesFor: this.currentUser.personalInformation && this.currentUser.personalInformation.useAhaSlidesFor,
      userProfession: this.currentUser.personalInformation && this.currentUser.personalInformation.userProfession,
    })
  },
  methods: {
    ...mapActions(['patchCurrentUserAction']),
    ...mapActions(['setIsOnboardingV3ShowingAction', 'setHasOnboardingV3Action']),
    ...mapActions('onBoarding', ['getFeatureBasedOptionsAction', 'copyFromTemplateAction']),
    preview(option) {
      this.previewSource = option && option.previewSource
    },
    async handleClickOnboardingOption(option) {
      if (!option || !option.presentationId) {
        this.createNewPresentation('From onboarding feature-based options')

        return
      }

      try {
        await this.copyFromTemplateAction({
          presentationId: option.presentationId,
          callback: this.handleCopyTemplateSuccess,
        })
      } catch (error) {
        Sentry.captureException(error)
      }
    },
    handleCopyTemplateSuccess(presentation) {
      const newPresentationId = presentation.id
      const params = { id: newPresentationId }
      this.markOnboardingDone()
      this.$router.push({ name: 'PresentationDetail', params })
    },
    skip() {
      this.markOnboardingDone()
      this.$router.push({ name: 'Presentations' })
    },
    browseTemplates() {
      this.setIsOnboardingV3ShowingAction(false)
      this.$router.push({ name: 'TemplateLibrary' })
    },
    markOnboardingDone() {
      this.setHasOnboardingV3Action(false)
      this.setIsOnboardingV3ShowingAction(false)
    },
    preloadAssets(assets) {
      utils.preloadOnboardingV5Assets(assets)
    },
  },
}
</script>

<style lang="scss" scoped>
.feature-based-options-container {
  @apply mx-auto px-4;
  @apply text-secondary-100;

  @media (min-width: 1366px) {
    max-width: 1280px;
  }
}

.section-wrapper {
  @apply grid grid-cols-1;

  @media (min-width: 1280px) {
    grid-template-columns: 340px 1fr;
  }
}

.section-options {
  width: 340px;
  @apply ml-auto mr-auto;
  @apply md:py-12 xl:py-0;
}

.section-preview {
  @apply hidden xl:block;
  @apply py-11 pl-24 pr-0;
}

.options-wrapper {
  @apply grid grid-cols-2;
  @apply gap-3 xs:gap-5;
}

.preview-card {
  @apply relative rounded-2xl overflow-hidden;
  aspect-ratio: 16/9;
}

.preview-source {
  @apply w-full h-full object-cover;

  &.default {
    @apply absolute inset-0;
    z-index: -1;
  }
}

.divider {
  position: relative;
  text-align: center;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    height: 1px;
    background-color: #D3D7E1;
    width: calc(50% - 24px);
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
  }
}

.fs-28 {
  font-size: 28px;
}
</style>
